import { drizzleAdapter } from '@better-auth/drizzle-adapter';
import { APIError, betterAuth } from 'better-auth';
import { mcp } from 'better-auth/plugins';

import { dailyGoals, defaultDailyGoal, profiles } from '@calorie-tracker/db';

import type { AppDatabase } from '@calorie-tracker/db';

import { mcpOidcScopes, resolveMcpResourceUrl } from './mcp.ts';
import { hashEdgePassword, verifyEdgePassword } from './password.ts';

export const authApiBasePath = '/api/auth';
const defaultUserTimezone = 'UTC';
const localHostAliases = new Set(['localhost', '127.0.0.1']);
const millisecondsPerDay = 24 * 60 * 60 * 1000;

export interface AuthRuntimeConfig {
  appName: string;
  baseURL: string;
  secret: string;
  database: AppDatabase;
  allowedEmailAddresses?: string | readonly string[] | undefined;
  mcpLoginPage?: string;
  mcpResourceUrl?: string;
}

interface AuthRequestContext {
  path?: string;
  body?: unknown;
}

export function normalizeEmailAddress(email: string): string {
  return email.trim().toLowerCase();
}

export function parseAllowedEmailAddresses(
  input: string | readonly string[] | undefined,
): Set<string> {
  const values =
    typeof input === 'string' ? input.split(/[\s,]+/) : Array.isArray(input) ? input : [];

  return new Set(
    values.map((email) => normalizeEmailAddress(email)).filter((email) => email.length > 0),
  );
}

export function isEmailAddressAllowed(
  email: string,
  allowedEmailAddresses: ReadonlySet<string>,
): boolean {
  return (
    allowedEmailAddresses.size === 0 || allowedEmailAddresses.has(normalizeEmailAddress(email))
  );
}

function getTrustedOrigins(baseURL: string): string[] {
  const url = new URL(baseURL);
  const origins = new Set([url.origin]);

  if (localHostAliases.has(url.hostname)) {
    for (const hostname of localHostAliases) {
      url.hostname = hostname;
      origins.add(url.origin);
    }
  }

  return [...origins];
}

export function createDefaultGoalEffectiveFrom(now: Date): string {
  return new Date(now.getTime() - millisecondsPerDay).toISOString().slice(0, 10);
}

export function createAuth(config: AuthRuntimeConfig) {
  const allowedEmailAddresses = parseAllowedEmailAddresses(config.allowedEmailAddresses);
  const mcpLoginPage = config.mcpLoginPage ?? '/';
  const mcpResource = resolveMcpResourceUrl(config.baseURL, config.mcpResourceUrl);

  return betterAuth({
    appName: config.appName,
    baseURL: config.baseURL,
    basePath: authApiBasePath,
    secret: config.secret,
    trustedOrigins: getTrustedOrigins(config.baseURL),
    database: drizzleAdapter(config.database, {
      provider: 'sqlite',
      usePlural: true,
    }),
    emailAndPassword: {
      enabled: true,
      password: {
        hash: hashEdgePassword,
        verify: verifyEdgePassword,
      },
    },
    plugins: [
      mcp({
        loginPage: mcpLoginPage,
        resource: mcpResource,
        oidcConfig: {
          loginPage: mcpLoginPage,
          allowDynamicClientRegistration: true,
          requirePKCE: true,
          defaultScope: 'openid profile email',
          scopes: [...mcpOidcScopes],
          metadata: {
            scopes_supported: [...mcpOidcScopes],
          },
        },
      }),
    ],
    hooks: {
      before: async (context) => {
        const requestContext = context as AuthRequestContext;

        if (requestContext.path !== '/sign-up/email' && requestContext.path !== '/sign-in/email') {
          return;
        }

        const body =
          typeof requestContext.body === 'object' && requestContext.body !== null
            ? requestContext.body
            : {};
        const email = 'email' in body && typeof body.email === 'string' ? body.email : undefined;

        if (email && !isEmailAddressAllowed(email, allowedEmailAddresses)) {
          throw new APIError('FORBIDDEN', {
            message: 'This email address is not allowed to access this app.',
            code: 'EMAIL_NOT_ALLOWED',
          });
        }
      },
    },
    databaseHooks: {
      user: {
        create: {
          async before(user) {
            const email = typeof user.email === 'string' ? user.email : undefined;

            if (!email) {
              return false;
            }

            return isEmailAddressAllowed(email, allowedEmailAddresses);
          },
          async after(user) {
            const now = new Date();
            const effectiveFrom = createDefaultGoalEffectiveFrom(now);

            await config.database.insert(profiles).values({
              id: crypto.randomUUID(),
              userId: user.id,
              displayName: typeof user.name === 'string' && user.name.length > 0 ? user.name : null,
              timezone: defaultUserTimezone,
              createdAt: now,
              updatedAt: now,
            });

            await config.database.insert(dailyGoals).values({
              id: crypto.randomUUID(),
              userId: user.id,
              calories: defaultDailyGoal.calories,
              proteinGrams: defaultDailyGoal.proteinGrams,
              carbsGrams: defaultDailyGoal.carbsGrams,
              fatGrams: defaultDailyGoal.fatGrams,
              effectiveFrom,
              effectiveTo: null,
              createdAt: now,
              updatedAt: now,
            });
          },
        },
      },
      session: {
        create: {
          async before(session) {
            if (allowedEmailAddresses.size === 0) {
              return;
            }

            const user = await config.database.query.users.findFirst({
              where: (table, { eq }) => eq(table.id, session.userId),
            });

            if (!user) {
              return false;
            }

            return isEmailAddressAllowed(user.email, allowedEmailAddresses);
          },
        },
      },
    },
  });
}

export type AppAuth = ReturnType<typeof createAuth>;
